/**
 * Readable labels for the CRM filter chips: the CRM's own name for a ``field:``
 * clause's field, and the owner's name for an ``owner:`` clause.
 *
 * Both come from the cached queries the CRM section's own controls read, so the
 * sidebar and the chip strip share one fetch. Nothing is requested for a CRM
 * that is not connected, for a CRM whose clauses name no field (or no owner),
 * or without an organization.
 */
import { useMemo } from 'react';

import { useConnectedSyncCrms } from '@/components/crm-sync-status/use-crm-sync-status.hook';
import {
  useGetCrmUsersQuery,
  useGetHubSpotPropertiesQuery,
  useGetSalesforcePropertiesQuery,
  type CrmFieldObject,
  type HubSpotPropertiesResponse,
} from '@/store/features/crm.feature';
import type { CrmFilterCrm, CrmFilterSections } from '@/types/search-filters';
import type { CrmFilterChipContext } from '@/utils/crm-filter-chips.util';
import {
  fieldLabelKey,
  parseFieldCondition,
} from '@/utils/crm-section-clauses.util';

type FieldLabels = NonNullable<CrmFilterChipContext['fieldLabels']>;

/** The record types a CRM section's ``field:`` clauses read. */
function fieldObjectsUsed(
  sections: CrmFilterSections | undefined,
  crm: CrmFilterCrm
): Set<CrmFieldObject> {
  const used = new Set<CrmFieldObject>();
  for (const clause of sections?.[crm] ?? []) {
    const parsed = parseFieldCondition(clause.condition ?? '');
    if (parsed) used.add(parsed.object);
  }
  return used;
}

function addLabels(
  into: Record<string, string>,
  object: CrmFieldObject,
  data: HubSpotPropertiesResponse | undefined
) {
  for (const property of data?.properties ?? [])
    into[fieldLabelKey(object, property.name)] =
      property.label || property.name;
}

/** ``{ salesforce: { 'lead.Status': 'Lead Status' } }`` for every field a
 *  clause names that the CRM has answered for. */
export function useCrmFieldLabels(
  sections: CrmFilterSections | undefined,
  organizationId: string
): FieldLabels {
  // A saved search, link or hand-off can name a CRM the organization has not
  // connected; its property list would only fail.
  const connected = useConnectedSyncCrms();
  const sf = fieldObjectsUsed(
    connected.some((c) => c.type === 'salesforce') ? sections : undefined,
    'salesforce'
  );
  const hs = fieldObjectsUsed(
    connected.some((c) => c.type === 'hubspot') ? sections : undefined,
    'hubspot'
  );
  const skip = (used: Set<CrmFieldObject>, object: CrmFieldObject) =>
    !organizationId || !used.has(object);
  const query = (objectType: string) => ({
    objectType,
    purpose: 'exclusion' as const,
    organizationId,
  });
  const { currentData: sfContact } = useGetSalesforcePropertiesQuery(
    query('Contact'),
    { skip: skip(sf, 'contact') }
  );
  const { currentData: sfLead } = useGetSalesforcePropertiesQuery(
    query('Lead'),
    { skip: skip(sf, 'lead') }
  );
  const { currentData: sfAccount } = useGetSalesforcePropertiesQuery(
    query('Account'),
    { skip: skip(sf, 'account') }
  );
  const { currentData: hsContact } = useGetHubSpotPropertiesQuery(
    query('contact'),
    { skip: skip(hs, 'contact') }
  );
  // HubSpot calls its accounts companies; it has no leads.
  const { currentData: hsCompany } = useGetHubSpotPropertiesQuery(
    query('company'),
    { skip: skip(hs, 'account') }
  );
  return useMemo(() => {
    const salesforce: Record<string, string> = {};
    addLabels(salesforce, 'contact', sfContact);
    addLabels(salesforce, 'lead', sfLead);
    addLabels(salesforce, 'account', sfAccount);
    const hubspot: Record<string, string> = {};
    addLabels(hubspot, 'contact', hsContact);
    addLabels(hubspot, 'account', hsCompany);
    const labels: FieldLabels = {};
    if (Object.keys(salesforce).length) labels.salesforce = salesforce;
    if (Object.keys(hubspot).length) labels.hubspot = hubspot;
    return labels;
  }, [sfContact, sfLead, sfAccount, hsContact, hsCompany]);
}

/** Owner names and field labels for the chip strip. ``enabled`` false asks for
 *  nothing. */
export function useCrmChipLabels(
  sections: CrmFilterSections | undefined,
  organizationId: string,
  enabled: boolean
): CrmFilterChipContext {
  const org = enabled ? organizationId : '';
  const connected = useConnectedSyncCrms();
  const wantsOwners = (crm: CrmFilterCrm) =>
    connected.some((c) => c.type === crm) &&
    (sections?.[crm] ?? []).some((c) => c.condition?.startsWith('owner:'));
  const { currentData: sfUsers } = useGetCrmUsersQuery(
    { crmType: 'salesforce', organizationId: org },
    { skip: !org || !wantsOwners('salesforce') }
  );
  const { currentData: hsUsers } = useGetCrmUsersQuery(
    { crmType: 'hubspot', organizationId: org },
    { skip: !org || !wantsOwners('hubspot') }
  );
  const fieldLabels = useCrmFieldLabels(enabled ? sections : undefined, org);
  return useMemo(() => {
    const names = (users: typeof sfUsers): Record<string, string> => {
      const map: Record<string, string> = {};
      for (const u of users?.users ?? [])
        map[u.crm_user_id] = u.name || u.email || u.crm_user_id;
      return map;
    };
    return {
      ownerNames: { salesforce: names(sfUsers), hubspot: names(hsUsers) },
      fieldLabels,
    };
  }, [sfUsers, hsUsers, fieldLabels]);
}
